import React, { useEffect, useRef, useState } from "react";

import {
  Navigate,
  useCookieConsent,
  useGetAnonymousForecast,
  useLocation,
  useMutateAnonymousForecast,
  useMutatePrepareAnonymousForecast,
  useNavigate,
  useParams,
  useSearchParams,
  useTranslation,
} from "utils";

import {
  AcquisitionCountrySelection,
  AcquisitionSoilType,
  COUNTRY_CODE,
  SOILSIGHT_ROUTE,
  SOILSIGHT_STEPS,
  SOILSIGHT_STEP_ROUTE,
  SoilSightStep,
  isAcquisitionCountrySupported,
} from "consts";

import { CalculateAnonymousForecastRequestBody } from "models";

import { AntModal } from "components";

import { PreLoginSoilSightFlow } from "pages/app/soilsight-pre-login/components/PreLoginSoilSightFlow";
import { ForecastStep } from "pages/app/soilsight-pre-login/components/steps/ForecastStep";
import { LocationStep, isLocationStepComplete } from "pages/app/soilsight-pre-login/components/steps/LocationStep";
import { RotationStep, isRotationStepComplete } from "pages/app/soilsight-pre-login/components/steps/RotationStep";
import { SignupOrLoginStep } from "pages/app/soilsight-pre-login/components/steps/SignupOrLoginStep";
import { formatAcquisitionZipForRequest } from "pages/app/soilsight-pre-login/utils/acquisitionLocation";
import {
  trackSoilSightSignupClick,
  trackSoilSightStepComplete,
  trackSoilSightStepView,
  trackSoilSightVirtualPageView,
} from "pages/app/soilsight-pre-login/utils/acquisitionTracking";
import { useSkipAcquisitionFlow } from "pages/app/soilsight-pre-login/utils/useSkipAcquisitionFlow";

const FIRST_STEP_ROUTE = SOILSIGHT_STEP_ROUTE(SOILSIGHT_STEPS[0]);
// A visitor who keeps picking cancels the pending run before it fires.
const FORECAST_DEBOUNCE_MS = 500;

// We keep the crop order. The forecast changes when the order changes.
const forecastBodyKey = ({ country, zip, cropRotationIds }: CalculateAnonymousForecastRequestBody): string =>
  [country, zip, cropRotationIds.join(",")].join("|");

export const SoilSightFlowOverlay = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("scenarioAnalysis");
  const hasCookieConsent = useCookieConsent();
  const forecastMutation = useMutateAnonymousForecast();
  const { abort: abortForecast } = forecastMutation;
  const prepareMutation = useMutatePrepareAnonymousForecast();
  const { isSkipEnabled, isResolved: isSkipFlagResolved } = useSkipAcquisitionFlow();
  const forecastDebounceTimeoutRef = useRef<number>();
  const lastRequestedForecastKeyRef = useRef("");
  const lastPreparedLocationRef = useRef("");

  const [maxStepIndex, setMaxStepIndex] = useState(0);
  const [country, setCountry] = useState<AcquisitionCountrySelection>(COUNTRY_CODE.GERMANY);
  const [zip, setZip] = useState("");
  const [soilType, setSoilType] = useState<AcquisitionSoilType | null>(null);
  const [cropIds, setCropIds] = useState<string[]>([]);
  const [forecastRequested, setForecastRequested] = useState(false);

  const formattedZip = formatAcquisitionZipForRequest(country, zip);
  const forecastBody = { country, zip: formattedZip, cropRotationIds: cropIds };
  const prepareBody = { country, zip: formattedZip };
  const canRunForecast = isAcquisitionCountrySupported(country);
  const forecastQuery = useGetAnonymousForecast(forecastBody, hasCookieConsent && forecastRequested && canRunForecast);

  const clearForecastDebounce = () => {
    window.clearTimeout(forecastDebounceTimeoutRef.current);
  };

  const discardForecast = () => {
    clearForecastDebounce();
    lastRequestedForecastKeyRef.current = "";
    setForecastRequested(false);
    forecastMutation.reset();
    // The hook does not register the first run, so this does nothing until a second one exists.
    abortForecast();
  };

  const requestForecast = (body: CalculateAnonymousForecastRequestBody) => {
    if (!canRunForecast || !isRotationStepComplete(body.cropRotationIds)) return;

    const key = forecastBodyKey(body);
    if (lastRequestedForecastKeyRef.current === key) {
      if (hasCookieConsent) {
        if (forecastQuery.isError) forecastQuery.refetch();
        return;
      }
      if (forecastMutation.isError) forecastMutation.mutate(body);
      return;
    }

    lastRequestedForecastKeyRef.current = key;

    if (hasCookieConsent) {
      setForecastRequested(true);
      return;
    }

    forecastMutation.mutate(body);
  };

  const requestForecastRef = useRef(requestForecast);
  requestForecastRef.current = requestForecast;

  const scheduleForecast = (body: CalculateAnonymousForecastRequestBody) => {
    clearForecastDebounce();
    forecastDebounceTimeoutRef.current = window.setTimeout(() => requestForecastRef.current(body), FORECAST_DEBOUNCE_MS);
  };

  const flushForecast = (body: CalculateAnonymousForecastRequestBody) => {
    clearForecastDebounce();
    requestForecast(body);
  };

  const handleCountryChange = (nextCountry: AcquisitionCountrySelection) => {
    setCountry(nextCountry);
    discardForecast();
  };

  const handleZipChange = (nextZip: string) => {
    setZip(nextZip);
    discardForecast();
  };

  const handleCropIdsChange = (nextCropIds: string[]) => {
    // Only the first complete rotation pre-warms the forecast. Later edits wait for the CTA, so
    // reshuffling does not fire a run per edit.
    const becameComplete = !isRotationStepComplete(cropIds) && isRotationStepComplete(nextCropIds);
    setCropIds(nextCropIds);
    discardForecast();
    if (becameComplete) scheduleForecast({ ...forecastBody, cropRotationIds: nextCropIds });
  };

  // A failed prepare still counts as this location's one attempt. The forecast never waits for it.
  const prepareLocation = () => {
    const key = `${country}|${formattedZip}`;
    if (!canRunForecast || lastPreparedLocationRef.current === key) return;
    lastPreparedLocationRef.current = key;
    prepareMutation.mutate(prepareBody);
  };

  const segment = (params["*"] ?? "").replace(/\/+$/, "");
  const isOpen = segment !== "";
  const requestedIndex = (SOILSIGHT_STEPS as readonly string[]).indexOf(segment);
  const step = SOILSIGHT_STEPS[requestedIndex];
  // The signup/login toggle lives in this query param, not local state. The title and back button
  // below only see the route.
  const isLoginMode = step === "signup" && searchParams.get("mode") === "login";
  // TODO(KH-1131): remove with the skip-acquisition-flow flag. The flag lets signup open without the steps before it.
  const isAheadOfWalkedSteps = step === "signup" && requestedIndex > maxStepIndex;
  const isDirectSignup = isSkipEnabled && isAheadOfWalkedSteps;
  // A fresh load renders before FeatBit is ready, so we hold a direct signup rather than bounce it to the first step.
  const isAwaitingSkipFlag = isAheadOfWalkedSteps && !isSkipFlagResolved;
  const isReachable = requestedIndex >= 0 && (requestedIndex <= maxStepIndex || isDirectSignup);
  const correctedPath = isOpen && !isReachable && !isAwaitingSkipFlag ? FIRST_STEP_ROUTE : null;

  const hasSeededHistory = useRef(false);
  const isColdEntry = isOpen && location.key === "default" && !hasSeededHistory.current;

  useEffect(() => {
    function seedColdEntryHistory() {
      if (!isColdEntry || isAwaitingSkipFlag) return;
      hasSeededHistory.current = true;
      // We seed the landing below the step, so browser back stays on-site for a shared link.
      navigate(SOILSIGHT_ROUTE, { replace: true });
      navigate(correctedPath ?? `${location.pathname}${location.search}`);
    }

    seedColdEntryHistory();
  }, [isColdEntry, isAwaitingSkipFlag, correctedPath, location.pathname, location.search, navigate]);

  useEffect(() => {
    function trackStepView() {
      // A closed overlay has no segment, so its index is -1 and `step` is undefined.
      if (isColdEntry || correctedPath || isAwaitingSkipFlag || !step) return;
      trackSoilSightVirtualPageView(step);
      trackSoilSightStepView(step);
    }

    trackStepView();
  }, [isColdEntry, correctedPath, isAwaitingSkipFlag, step]);

  useEffect(() => {
    function abandonForecast() {
      window.clearTimeout(forecastDebounceTimeoutRef.current);
      abortForecast();
    }

    return abandonForecast;
  }, [abortForecast]);

  const closeOverlay = () => navigate(SOILSIGHT_ROUTE, { replace: true });

  const goBack = () => {
    if (isLoginMode) {
      const next = new URLSearchParams(searchParams);
      next.delete("mode");
      setSearchParams(next);
      return;
    }
    if (isDirectSignup) {
      closeOverlay();
      return;
    }
    if (requestedIndex <= 0) return;
    navigate(SOILSIGHT_STEP_ROUTE(SOILSIGHT_STEPS[requestedIndex - 1]));
  };

  const advance = () => {
    const nextIndex = requestedIndex + 1;
    if (nextIndex >= SOILSIGHT_STEPS.length || !step) return;
    trackSoilSightStepComplete(step);
    setMaxStepIndex((currentMax) => Math.max(currentMax, nextIndex));
    navigate(SOILSIGHT_STEP_ROUTE(SOILSIGHT_STEPS[nextIndex]));
  };

  const isForecastLoading = hasCookieConsent ? forecastQuery.isFetching : forecastMutation.isPending;
  const isForecastError = hasCookieConsent ? forecastQuery.isError : forecastMutation.isError;
  const forecastError = hasCookieConsent ? forecastQuery.error : forecastMutation.error;
  const forecast = (hasCookieConsent ? forecastQuery.data : forecastMutation.data) ?? null;

  if (!isOpen) return null;
  if (isColdEntry || isAwaitingSkipFlag) return null;
  if (correctedPath) return <Navigate to={correctedPath} replace />;

  // `ownsCta` keeps the shell footer out when the step renders its own call to action.
  const stepConfig: Record<SoilSightStep, { body: () => React.ReactNode; isComplete: boolean; ownsCta?: boolean; onLeave?: () => void }> = {
    location: {
      body: () => (
        <LocationStep
          country={country}
          zip={zip}
          soilType={soilType}
          onCountryChange={handleCountryChange}
          onZipChange={handleZipChange}
          onSoilTypeChange={setSoilType}
        />
      ),
      isComplete: isLocationStepComplete(country, zip, soilType),
      onLeave: prepareLocation,
    },
    rotation: {
      body: () => <RotationStep cropIds={cropIds} onCropIdsChange={handleCropIdsChange} />,
      isComplete: isRotationStepComplete(cropIds),
      onLeave: () => flushForecast(forecastBody),
    },
    forecast: {
      body: () => (
        <ForecastStep
          isLoading={isForecastLoading}
          isError={isForecastError}
          error={forecastError}
          forecast={forecast}
          onRetry={() => flushForecast(forecastBody)}
          onCreateAccount={() => {
            trackSoilSightSignupClick("forecast");
            advance();
          }}
        />
      ),
      isComplete: true,
      ownsCta: true,
    },
    signup: {
      body: () => <SignupOrLoginStep />,
      isComplete: true,
      ownsCta: true,
    },
  };

  const current = stepConfig[step];

  const handlePrimaryCta = () => {
    current.onLeave?.();
    advance();
  };

  return (
    <AntModal
      open
      title={isLoginMode ? t("preLogin.steps.login") : t(`preLogin.steps.${step}`)}
      onCancel={closeOverlay}
      footer={null}
      width="100%"
      className="soilsight-flow-overlay"
      wrapClassName="soilsight-flow-overlay-wrap"
      closable={false}
      keyboard
      maskClosable={false}
      data-testid="soilsight-flow-overlay"
    >
      <PreLoginSoilSightFlow
        stepIndex={requestedIndex}
        onClose={closeOverlay}
        onBack={goBack}
        onPrimaryCta={handlePrimaryCta}
        isPrimaryCtaDisabled={!current.isComplete}
        showPrimaryCta={!current.ownsCta}
        showStepper={!isDirectSignup}
      >
        {current.body()}
      </PreLoginSoilSightFlow>
    </AntModal>
  );
};
